import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AbstractControl, FormArray, FormBuilder, Validators } from '@angular/forms';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { BreakpointObserver } from '@angular/cdk/layout';

import { catchError, finalize, of, Subscription, tap } from 'rxjs';
import { DeviceDetectorService } from 'ngx-device-detector';
import { v4 } from 'uuid';

import { FieldConfig } from '@shared/restaurant/models';
import { FieldType } from '@shared/restaurant/enums';
import { StringKeyDictionary } from '@shared/types';
import { AppIcon, AppIconColor } from '@tablesready/frontend-icons';
import { AuthRestaurantService } from '@tablesready/frontend-store';
import { AlertsService, IAlertTypes } from '@tablesready/frontend-alerts-bar';
import {
  WhoService,
  PricePlansRestrictionsService,
  PricePlansRestrictionsModalService,
} from '../../../services';
import {
  TABLET_BREAKPOINT,
  MAX_OPTIONS_TO_DISPLAY,
  MAX_OPTIONS_TO_DISPLAY_TABLET,
} from './constants';
import { CustomFieldsComponent } from './components';

@Component({
  selector: 'tablesready-party-fields',
  templateUrl: './party-fields.component.html',
  styleUrls: ['./party-fields.component.scss'],
})
export class PartyFieldsComponent implements OnInit, OnDestroy {
  @ViewChild('partyCustomFields', { read: CustomFieldsComponent }) customFieldsRef?: CustomFieldsComponent;

  controls: FormArray;
  icon = AppIcon;
  color = AppIconColor;
  submitting = false;
  savedCustomFields: string[] = [];
  isDesktop = true;
  sideMenuWidth = 0;
  maxSelectOptionsToDisplay = MAX_OPTIONS_TO_DISPLAY;
  reservationPlural = '';

  private customFields: FieldConfig[];
  private isLocationOnBusinessPlan = false;
  private readonly subscriptions = new Subscription();

  constructor(
    private readonly router: Router,
    private readonly formBuilder: FormBuilder,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly whoService: WhoService,
    private readonly alertsService: AlertsService,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly deviceDetectorService: DeviceDetectorService,
    private readonly pricePlansRestrictionsService: PricePlansRestrictionsService,
    private readonly pricePlansRestrictionsModalService: PricePlansRestrictionsModalService,
  ) {
  }

  ngOnInit(): void {
    this.controls = this.formBuilder.array([] ,[uniqueKeyValidator]);

    const authRestaurantSubscription = this.authRestaurantService.select()
      .pipe(tap(({ account, location }) => {
        const { partyFields, displayOptions } = location;

        this.isLocationOnBusinessPlan = this.pricePlansRestrictionsService.availableForBusinessPlan(account.subscriptionType, location.recurly);
        this.reservationPlural = displayOptions.labels.reservation_plural;

        if (this.isLocationOnBusinessPlan) {
          this.customFields = partyFields;
          this.setFields(this.customFields);
        }
      }))
      .subscribe();
    this.subscriptions.add(authRestaurantSubscription);

    const deviceInfo = this.deviceDetectorService.getDeviceInfo();
    const isIPad = deviceInfo.device === 'Macintosh' && deviceInfo.deviceType === 'tablet';
    const tabletBreakpoint = isIPad ? TABLET_BREAKPOINT * 2 : TABLET_BREAKPOINT;
    const breakpointObserverSub = this.breakpointObserver.observe([`(max-width: ${tabletBreakpoint}px)`])
      .pipe(
        tap(({ matches: isTablet }) => {
          this.isDesktop = !isTablet;
          this.maxSelectOptionsToDisplay = isTablet ? MAX_OPTIONS_TO_DISPLAY_TABLET : MAX_OPTIONS_TO_DISPLAY;
        })
      ).subscribe();
    this.subscriptions.add(breakpointObserverSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  add(value?: FieldConfig): void {
    if (!this.isLocationOnBusinessPlan) {
      this.pricePlansRestrictionsModalService.display(this.authRestaurantService.get())
        .pipe(tap(() => this.customFieldsRef.close())).subscribe();

      return;
    }

    const group = this.formBuilder.group({
      id: [value?.id || v4(), [Validators.required]],
      name: [value?.name || '', [Validators.required, Validators.maxLength(50)]],
      placeholder: [value?.placeholder || '', [Validators.maxLength(100)]],
      type: [value?.type || FieldType.text, [Validators.required]], // TODO: add in array validation
      enabled: [value?.enabled || false, [Validators.required]],
      options: [value?.options || [], []], // TODO: options are required only if type is
      visibility: this.formBuilder.group({
        public: this.formBuilder.group({
          view: [value?.visibility?.public?.view || false, [Validators.required]],
          add: [value?.visibility?.public?.add || false, [Validators.required]],
          required: [value?.visibility?.public?.required || false, [Validators.required]],
        }),
        private: this.formBuilder.group({
          view: [value?.visibility?.private?.view || false, [Validators.required]],
          add: [value?.visibility?.private?.add || false, [Validators.required]],
          required: [value?.visibility?.private?.required || false, [Validators.required]],
        }),
      }),
      customer: [value?.customer || false, [Validators.required]],
    });

    const typeSub = group.get('type').valueChanges
      .pipe(
        tap(value => {
          const optionsControl = group.get('options');
          optionsControl.removeValidators(Validators.required);

          if (value === FieldType.select) {
            optionsControl.setValidators(Validators.required);
          }
        }),
      ).subscribe();
    this.subscriptions.add(typeSub);

    this.controls.push(group);
  }

  remove(index: number): void {
    this.controls.removeAt(index);
    this.controls.markAsDirty({onlySelf: true});
  }

  submit() {
    if (this.submitting) {
      return;
    }

    this.submitting = true;
    this.whoService.partyFields(this.controls.value)
      .pipe(
        finalize(() => {
          this.alertsService.addSuccess('fields data updated.', 'Success: ');
          this.submitting = false;
          this.controls.markAsPristine();
        }),
        catchError(error => {
          this.alertsService.add({
            type: IAlertTypes.danger,
            heading: 'Settings:',
            body: error,
          });

          return of(null);
        }),
      )
      .subscribe();
  }

  moveField(event: CdkDragDrop<string[]>): void {
    const control = this.controls.at(event.previousIndex);
    this.controls.removeAt(event.previousIndex);
    this.controls.insert(event.currentIndex, control);
    this.controls.markAsDirty({onlySelf: true});
  }

  resetCustomFields(): void {
    this.setFields(this.customFields);
  }

  private setFields(fields: FieldConfig[]): void {
    this.controls.clear();
    this.savedCustomFields = [];
    for (let i = 0; i < fields.length; i++) {
      this.savedCustomFields.push(fields[i].id);
      this.add(fields[i]);
    }
    this.controls.markAsPristine();
  }

  protected readonly location = location;
}

function uniqueKeyValidator(control: AbstractControl) {
  if (!control.value) {
    return null;
  }

  const map: StringKeyDictionary<boolean> = {};
  for (const field of control.value) {
    if (map[field.id]) {
      return {uniqueId: true};
    }

    map[field.id] = true;
  }

  return null;
}
